import { useRef, useCallback } from 'react';

/**
 * Hook para acumular respuestas de streaming por batches
 *
 * Responsabilidades:
 * - Mantener Maps para respuestas completadas y progreso por batch
 * - Mantener Map para citas por batch
 * - Proveer funciones para acumular texto y citas
 * - Proveer función para obtener texto y citas acumuladas
 *
 * @returns Estado y funciones para acumulación de batches
 */

export interface BatchAccumulatorState {
  completedBatches: Map<number, string>;
  batchProgress: Map<number, string>;
  batchCitations: Map<number, Record<string, unknown>>;
}

export function useBatchAccumulator() {
  // Maps para acumulación de batches
  const completedBatchesRef = useRef<Map<number, string>>(new Map());
  const batchProgressRef = useRef<Map<number, string>>(new Map());
  const batchCitationsRef = useRef<Map<number, Record<string, unknown>>>(new Map());
  const lastVisibleTextRef = useRef<string>('');
  const currentBatchRef = useRef<number | null>(null);

  /**
   * Reset all batch state for a new streaming session
   */
  const reset = useCallback(() => {
    completedBatchesRef.current = new Map();
    batchProgressRef.current = new Map();
    batchCitationsRef.current = new Map();
    lastVisibleTextRef.current = '';
    currentBatchRef.current = null;
  }, []);

  /**
   * Handle batch start - save progress from previous batch if switching
   */
  const handleBatchStart = useCallback((batchNumber: number, totalBatches: number) => {
    const currentBatch = currentBatchRef.current;

    // If switching batches, save progress from previous batch
    if (currentBatch !== null && currentBatch !== batchNumber) {
      const previousProgress = batchProgressRef.current.get(currentBatch);
      if (previousProgress && !completedBatchesRef.current.has(currentBatch)) {
        completedBatchesRef.current.set(currentBatch, previousProgress);
      }
    }

    currentBatchRef.current = batchNumber;
    return { currentBatch: batchNumber, totalBatches };
  }, []);

  /**
   * Accumulate text from token event
   */
  const accumulateText = useCallback((batchNumber: number, progressText: string): string => {
    const currentBatch = currentBatchRef.current;

    // Handle batch switch during token streaming
    if (currentBatch !== null && currentBatch !== batchNumber) {
      const previousProgress = batchProgressRef.current.get(currentBatch);
      if (previousProgress && !completedBatchesRef.current.has(currentBatch)) {
        completedBatchesRef.current.set(currentBatch, previousProgress);
      }
    }

    currentBatchRef.current = batchNumber;

    // Store current progress
    if (progressText) {
      batchProgressRef.current.set(batchNumber, progressText);
    }

    // Build accumulated text from all batches
    const accumulatedParts: string[] = [];

    // Add completed batches before current
    for (let b = 1; b < batchNumber; b++) {
      const completedResponse = completedBatchesRef.current.get(b);
      if (completedResponse) {
        accumulatedParts.push(completedResponse);
      }
    }

    // Add current progress
    if (progressText) {
      accumulatedParts.push(progressText);
    }

    let accumulatedText = accumulatedParts.join('\n\n');

    // Preserve last visible text if no parts available
    if (accumulatedParts.length > 0) {
      lastVisibleTextRef.current = accumulatedText;
    } else if (lastVisibleTextRef.current.length > 0) {
      accumulatedText = lastVisibleTextRef.current;
    }

    return accumulatedText;
  }, []);

  /**
   * Mark a batch as complete with its final response
   */
  const completeBatch = useCallback(
    (batchNumber: number, response: string, citations?: Record<string, unknown>) => {
      if (response) {
        completedBatchesRef.current.set(batchNumber, response);
        batchProgressRef.current.delete(batchNumber);
      }

      if (citations && Object.keys(citations).length > 0) {
        batchCitationsRef.current.set(batchNumber, citations);
      }
    },
    []
  );

  /**
   * Get accumulated text from all completed batches up to and including batchNumber
   */
  const getAccumulatedText = useCallback((upToBatch: number): string => {
    const accumulatedParts: string[] = [];

    for (let b = 1; b <= upToBatch; b++) {
      const batchResponse = completedBatchesRef.current.get(b);
      if (batchResponse) {
        accumulatedParts.push(batchResponse);
      }
    }

    return accumulatedParts.join('\n\n');
  }, []);

  /**
   * Get all accumulated citations merged from all batches
   */
  const getAccumulatedCitations = useCallback((): Record<string, unknown> => {
    const merged: Record<string, unknown> = {};
    const sortedBatchNums = Array.from(batchCitationsRef.current.keys()).sort((a, b) => a - b);

    for (const batchNum of sortedBatchNums) {
      const batchCits = batchCitationsRef.current.get(batchNum) || {};
      Object.assign(merged, batchCits);
    }

    return merged;
  }, []);

  /**
   * Store citations for a specific batch
   */
  const storeCitations = useCallback((batchNumber: number, citations: Record<string, unknown>) => {
    if (citations && Object.keys(citations).length > 0) {
      batchCitationsRef.current.set(batchNumber, citations);
    }
  }, []);

  return {
    // State access
    completedBatches: completedBatchesRef.current,
    batchProgress: batchProgressRef.current,
    batchCitations: batchCitationsRef.current,
    currentBatch: currentBatchRef.current,

    // Functions
    reset,
    handleBatchStart,
    accumulateText,
    completeBatch,
    getAccumulatedText,
    getAccumulatedCitations,
    storeCitations,
  };
}
